import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { MainLayout } from 'views/layouts'
import { H2, P } from 'views/components/UI/Text'
import getOneCompany from 'services/companies/getOneCompany'
import { useNotification } from 'lib/context/notification.context'
import {
  createLuminaTeam,
  deleteLuminaTeam,
  getEligibleLuminaMembers,
  getLuminaPeople,
  getLuminaTeam,
  getLuminaTeams,
  LuminaMember,
  LuminaPerson,
  LuminaTeam,
  updateLuminaTeam,
  updateLuminaTeamOrganization,
  updateLuminaPersonCargo,
  updateLuminaPersonDesempeno,
  syncTeamWithLumina,
  syncTeamWithNinebox,
  requestLuminaDescriptiveMapsInfo,
  requestLuminaIntegrationInfo,
} from './services'

interface TeamFormState {
  id?: number
  name: string
  description: string
  memberIds: number[]
}

const emptyForm: TeamFormState = { name: '', description: '', memberIds: [] }

// Lumina opens a private map for the team owner.

interface OrganizationNodeProps {
  member: LuminaMember
  members: LuminaMember[]
  childrenByManager: Map<number | null, LuminaMember[]>
  getForbiddenManagerIds: (memberId: number) => Set<number>
  onManagerChange: (memberId: number, managerId: number | null) => void
}

const OrganizationNode = ({
  member,
  members,
  childrenByManager,
  getForbiddenManagerIds,
  onManagerChange,
}: OrganizationNodeProps) => {
  const children = childrenByManager.get(member.member_id) || []
  const forbiddenManagerIds = getForbiddenManagerIds(member.member_id)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 250, position: 'relative' }}>
      <Box
        sx={{
          width: 250,
          padding: 2,
          border: '1px solid #c9dcea',
          borderTop: '4px solid #2d749c',
          borderRadius: 2,
          background: '#fff',
          boxShadow: '0 8px 18px rgba(22, 50, 71, 0.1)',
        }}
      >
        <Typography fontWeight={700} color="#17374D">
          {member.full_name}
        </Typography>
        <Typography variant="body2" color="text.secondary" noWrap>
          {member.email || member.document || 'Sin dato'}
        </Typography>
        <FormControl fullWidth size="small" sx={{ marginTop: 1.5 }}>
          <InputLabel id={`manager-${member.member_id}`}>Reporta a</InputLabel>
          <Select
            labelId={`manager-${member.member_id}`}
            label="Reporta a"
            value={member.manager_member_id || ''}
            onChange={(event) =>
              onManagerChange(member.member_id, event.target.value ? Number(event.target.value) : null)
            }
          >
            <MenuItem value="">Sin coordinador</MenuItem>
            {members
              .filter((candidate) => !forbiddenManagerIds.has(candidate.member_id))
              .map((candidate) => (
                <MenuItem key={candidate.member_id} value={candidate.member_id}>
                  {candidate.full_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Box>
      {children.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            paddingTop: 4,
            marginTop: 2,
            borderTop: '2px solid #9bc5dc',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -18,
              left: '50%',
              height: 18,
              borderLeft: '2px solid #9bc5dc',
            },
          }}
        >
          {children.map((child) => (
            <OrganizationNode
              key={child.member_id}
              member={child}
              members={members}
              childrenByManager={childrenByManager}
              getForbiddenManagerIds={getForbiddenManagerIds}
              onManagerChange={onManagerChange}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

const LuminaAnalytics = () => {
  // Build marker: staging integration verification.
  const { getError, getSuccess } = useNotification()
  const [tab, setTab] = useState(0)
  const [teams, setTeams] = useState<LuminaTeam[]>([])
  const [people, setPeople] = useState<LuminaPerson[]>([])
  const [peopleSearch, setPeopleSearch] = useState('')
  const [peopleLoading, setPeopleLoading] = useState(false)
  const [baremoDrafts, setBaremoDrafts] = useState<Record<number, string>>({})
  const [baremoErrors, setBaremoErrors] = useState<Record<number, string>>({})
  const [cargoDrafts, setCargoDrafts] = useState<Record<number, string>>({})
  const [members, setMembers] = useState<LuminaMember[]>([])
  const [memberSearch, setMemberSearch] = useState('')
  const [membersLoading, setMembersLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<TeamFormState>(emptyForm)
  const [organizationTeamId, setOrganizationTeamId] = useState<number | ''>('')
  const [organizationTeam, setOrganizationTeam] = useState<LuminaTeam | null>(null)
  const [organizationLoading, setOrganizationLoading] = useState(false)
  const [organizationSaving, setOrganizationSaving] = useState(false)
  const [syncingTeamId, setSyncingTeamId] = useState<number | null>(null)
  const [syncingNineboxTeamId, setSyncingNineboxTeamId] = useState<number | null>(null)
  const savingPersonFields = useRef<Set<string>>(new Set())
  const { role, companyId } = useSelector((state: any) => ({ role: state.auth.role, companyId: state.user.companyId }))
  const userEmail = useSelector((state: any) => state.user.email || state.auth.email || '')
  const [companyData, setCompanyData] = useState<any>(null)
  const [requestingInfo, setRequestingInfo] = useState(false)
  // Fail closed: leaders must not see the module until the company capability is confirmed.
  const [descriptiveMapsEnabled, setDescriptiveMapsEnabled] = useState(false)

  useEffect(() => {
    if (companyId) {
      getOneCompany(companyId)
        .then((response: any) => {
          setCompanyData(response?.data || null)
          setDescriptiveMapsEnabled(response?.data?.descriptive_maps_enabled === true)
        })
        .catch(() => setDescriptiveMapsEnabled(false))
    }
  }, [companyId, role])

  const handleRequestDescriptiveMapsInfo = async () => {
    try {
      setRequestingInfo(true)
      await requestLuminaDescriptiveMapsInfo({
        mail: userEmail || 'solicitud@bluesitehr.com',
        type: 'lumina_descriptive_maps_request',
        company: companyData?.name || '',
        contact_name: companyData?.contact_name || '',
        contact_email: companyData?.contact_email || '',
        contact_phone: companyData?.phone || companyData?.contact_phone || '',
        internal_only: true,
      })
      getSuccess('Solicitud enviada correctamente.')
    } catch {
      getError('No fue posible enviar la solicitud. Inténtalo de nuevo.')
    } finally {
      setRequestingInfo(false)
    }
  }

  const handleRequestIntegrationInfo = async () => {
    try {
      setRequestingInfo(true)
      await requestLuminaIntegrationInfo({
        mail: 'sararuiz@bluesitehr.com',
        type: 'lumina_integration_request',
        company: companyData?.name || '',
        message: 'Solicitud de integración con otra plataforma para que Bluesite pueda consumir sus datos.',
        contact_name: companyData?.contact_name || '',
        contact_email: companyData?.contact_email || '',
        contact_phone: companyData?.phone || companyData?.contact_phone || '',
        internal_only: false,
      })
      getSuccess('Solicitud de integración enviada correctamente.')
    } catch {
      getError('No fue posible enviar la solicitud de integración. Inténtalo de nuevo.')
    } finally {
      setRequestingInfo(false)
    }
  }

  const loadTeams = useCallback(async () => {
    try {
      setLoading(true)
      setTeams(await getLuminaTeams())
    } catch {
      getError('No fue posible cargar los equipos.')
    } finally {
      setLoading(false)
    }
  }, [getError])

  const handleSyncTeam = async (team: LuminaTeam) => {
    // Abrimos la pestaña durante el gesto del usuario y conservamos la referencia
    // para poder navegarla cuando el backend devuelva el enlace privado.
    const launchWindow = window.open('', '_blank')
    try {
      setSyncingTeamId(team.id)
      const result = await syncTeamWithLumina(team.id)
      const launchUrl = result?.data?.launch_url || result?.launch_url
      if (!launchUrl) {
        launchWindow?.close()
        throw new Error('Mapas Descriptivos no devolvió un enlace privado para abrir el mapa.')
      }
      getSuccess(`El equipo “${team.name}” fue enviado a Mapas Descriptivos.`)
      if (launchWindow && !launchWindow.closed) {
        launchWindow.location.href = launchUrl
      } else {
        window.open(launchUrl, '_blank')
      }
    } catch (error: any) {
      // Keep the diagnostic tab open while troubleshooting integration failures.
      // The backend error is shown in the main window below.
      getError(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'No se pudo enviar el equipo a Mapas Descriptivos. Inténtalo de nuevo o contacta con soporte.'
      )
    } finally {
      setSyncingTeamId(null)
    }
  }

  const handleSyncNineboxTeam = async (team: LuminaTeam) => {
    const launchWindow = window.open('', '_blank')
    try {
      setSyncingNineboxTeamId(team.id)
      const result = await syncTeamWithNinebox(team.id)
      const launchUrl = result?.data?.launch_url || result?.launch_url
      if (!launchUrl) {
        launchWindow?.close()
        throw new Error('9Box no devolvió un enlace privado para abrir la matriz.')
      }
      getSuccess(`El equipo “${team.name}” fue enviado a 9Box.`)
      if (launchWindow && !launchWindow.closed) {
        launchWindow.location.href = launchUrl
      } else {
        window.open(launchUrl, '_blank')
      }
    } catch (error: any) {
      launchWindow?.close()
      getError(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          'No se pudo generar el 9Box del equipo. Inténtalo de nuevo o contacta con soporte.'
      )
    } finally {
      setSyncingNineboxTeamId(null)
    }
  }

  useEffect(() => {
    if (!descriptiveMapsEnabled) {
      setTeams([])
      setLoading(false)
      return
    }

    loadTeams()
  }, [descriptiveMapsEnabled, loadTeams])

  const loadPeople = useCallback(
    async (search = '') => {
      try {
        setPeopleLoading(true)
        const loadedPeople = await getLuminaPeople(search)
        setPeople(loadedPeople)
        setBaremoDrafts(
          Object.fromEntries(
            loadedPeople.map((person) => [person.user_id, person.desempeno == null ? '' : String(person.desempeno)])
          )
        )
        setCargoDrafts(Object.fromEntries(loadedPeople.map((person) => [person.user_id, person.cargo || ''])))
        setBaremoErrors({})
      } catch {
        getError('No fue posible cargar las personas de Lumina.')
      } finally {
        setPeopleLoading(false)
      }
    },
    [getError]
  )

  useEffect(() => {
    if (tab !== 0) return

    // Keep the people view empty until the administrator searches. This avoids
    // loading every eligible worker for companies with thousands of users.
    if (!peopleSearch.trim()) {
      setPeople([])
      setBaremoDrafts({})
      setCargoDrafts({})
      setBaremoErrors({})
      setPeopleLoading(false)
      return
    }

    const timeoutId = window.setTimeout(() => loadPeople(peopleSearch), 300)
    return () => window.clearTimeout(timeoutId)
  }, [tab, loadPeople, peopleSearch])

  const loadEligibleMembers = useCallback(
    async (search = '') => {
      try {
        setMembersLoading(true)
        setMembers(await getEligibleLuminaMembers(search))
      } catch {
        getError('No fue posible cargar las personas elegibles.')
      } finally {
        setMembersLoading(false)
      }
    },
    [getError]
  )

  useEffect(() => {
    if (!dialogOpen) return
    if (!memberSearch.trim()) return
    const timeoutId = window.setTimeout(() => loadEligibleMembers(memberSearch.trim()), 300)
    return () => window.clearTimeout(timeoutId)
  }, [dialogOpen, loadEligibleMembers, memberSearch])

  const loadOrganization = useCallback(
    async (teamId: number) => {
      try {
        setOrganizationLoading(true)
        setOrganizationTeam(await getLuminaTeam(teamId))
      } catch {
        getError('No fue posible cargar el organigrama del equipo.')
      } finally {
        setOrganizationLoading(false)
      }
    },
    [getError]
  )

  useEffect(() => {
    if (tab !== 2 || !organizationTeamId) return
    loadOrganization(organizationTeamId)
  }, [tab, organizationTeamId, loadOrganization])

  const openCreate = () => {
    setMemberSearch('')
    setMembers([])
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEdit = async (teamId: number) => {
    try {
      const team = await getLuminaTeam(teamId)
      setMemberSearch('')
      setMembers(team.members || [])
      setForm({
        id: team.id,
        name: team.name,
        description: team.description || '',
        memberIds: team.members?.map((member) => member.user_id) || [],
      })
      setDialogOpen(true)
    } catch {
      getError('No fue posible cargar el equipo.')
    }
  }

  const toggleMember = (userId: number) =>
    setForm((current) => ({
      ...current,
      memberIds: current.memberIds.includes(userId)
        ? current.memberIds.filter((id) => id !== userId)
        : [...current.memberIds, userId],
    }))

  const saveTeam = async () => {
    if (!form.name.trim()) {
      getError('El nombre del equipo es obligatorio.')
      return
    }
    try {
      setSaving(true)
      const payload = { name: form.name.trim(), description: form.description.trim(), member_ids: form.memberIds }
      if (form.id) {
        await updateLuminaTeam(form.id, payload)
        getSuccess('Equipo actualizado correctamente.')
      } else {
        await createLuminaTeam(payload)
        getSuccess('Equipo creado correctamente.')
      }
      setDialogOpen(false)
      await loadTeams()
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el equipo.')
    } finally {
      setSaving(false)
    }
  }

  const removeTeam = async (team: LuminaTeam) => {
    if (!window.confirm(`Se eliminará definitivamente el equipo “${team.name}”.`)) return
    try {
      await deleteLuminaTeam(team.id)
      if (organizationTeamId === team.id) {
        setOrganizationTeamId('')
        setOrganizationTeam(null)
      }
      getSuccess('Equipo eliminado correctamente.')
      await loadTeams()
    } catch {
      getError('No fue posible eliminar el equipo.')
    }
  }

  const teamMembers = organizationTeam?.members
  const organizationMembers = useMemo(() => teamMembers || [], [teamMembers])
  const childrenByManager = useMemo(() => {
    const children = new Map<number | null, LuminaMember[]>()
    organizationMembers.forEach((member) => {
      const managerId = member.manager_member_id || null
      children.set(managerId, [...(children.get(managerId) || []), member])
    })
    return children
  }, [organizationMembers])
  const organizationRoots = childrenByManager.get(null) || []
  const getForbiddenManagerIds = (memberId: number) => {
    const forbiddenIds = new Set([memberId])
    const pendingIds = [memberId]

    while (pendingIds.length > 0) {
      const currentId = pendingIds.pop() as number
      ;(childrenByManager.get(currentId) || []).forEach((child) => {
        forbiddenIds.add(child.member_id)
        pendingIds.push(child.member_id)
      })
    }

    return forbiddenIds
  }

  const changeManager = (memberId: number, managerId: number | null) => {
    setOrganizationTeam((current) =>
      current
        ? {
            ...current,
            members: (current.members || []).map((member) =>
              member.member_id === memberId ? { ...member, manager_member_id: managerId } : member
            ),
          }
        : current
    )
  }

  const saveOrganization = async () => {
    if (!organizationTeam) return
    try {
      setOrganizationSaving(true)
      const team = await updateLuminaTeamOrganization(organizationTeam.id, {
        relationships: (organizationTeam.members || []).map((member) => ({
          member_id: member.member_id,
          manager_member_id: member.manager_member_id || null,
        })),
      })
      setOrganizationTeam(team)
      getSuccess('Organigrama guardado correctamente.')
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el organigrama.')
    } finally {
      setOrganizationSaving(false)
    }
  }

  const saveBaremo = async (person: LuminaPerson) => {
    const value = baremoDrafts[person.user_id]?.trim() || ''
    const numericValue = value === '' ? null : Number(value.replace(',', '.'))
    const currentValue = person.desempeno == null ? null : Number(person.desempeno)
    const fieldKey = `${person.user_id}:desempeno`
    const isValidValue =
      numericValue === null ||
      (Number.isFinite(numericValue) &&
        numericValue >= 1 &&
        numericValue <= 10 &&
        /^\d{1,2}([.,]\d{1,2})?$/.test(value))
    if (!isValidValue) {
      setBaremoErrors((current) => ({
        ...current,
        [person.user_id]: 'Debe estar entre 1,00 y 10,00, con un máximo de dos decimales.',
      }))
      getError('El desempeño debe ser un número entre 1,00 y 10,00 con un máximo de dos decimales.')
      return
    }

    if (numericValue === currentValue || savingPersonFields.current.has(fieldKey)) return

    try {
      savingPersonFields.current.add(fieldKey)
      await updateLuminaPersonDesempeno(person.user_id, numericValue)
      setPeople((current) =>
        current.map((item) => (item.user_id === person.user_id ? { ...item, desempeno: numericValue } : item))
      )
      setBaremoErrors((current) => ({ ...current, [person.user_id]: '' }))
      getSuccess('Desempeño guardado correctamente.')
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el desempeño.')
    } finally {
      savingPersonFields.current.delete(fieldKey)
    }
  }

  const saveCargo = async (person: LuminaPerson) => {
    const cargo = cargoDrafts[person.user_id]?.trim() || ''
    const fieldKey = `${person.user_id}:cargo`
    if (cargo === (person.cargo || '').trim() || savingPersonFields.current.has(fieldKey)) return

    try {
      savingPersonFields.current.add(fieldKey)
      await updateLuminaPersonCargo(person.user_id, cargo)
      setPeople((current) =>
        current.map((item) => (item.user_id === person.user_id ? { ...item, cargo: cargo || null } : item))
      )
      getSuccess('Cargo guardado correctamente.')
    } catch (error: any) {
      getError(error?.response?.data?.message || 'No fue posible guardar el cargo.')
    } finally {
      savingPersonFields.current.delete(fieldKey)
    }
  }

  if (!descriptiveMapsEnabled) {
    return (
      <MainLayout>
        <H2>Mapas Descriptivos</H2>
        <Box
          sx={{
            maxWidth: 860,
            mx: 'auto',
            mt: 6,
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            background: '#fff',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              maxWidth: 1050,
              mx: 'auto',
              mb: 2.5,
              color: '#c90072',
              fontSize: { xs: '2rem', md: '2.75rem' },
              fontWeight: 800,
              lineHeight: 1.12,
              letterSpacing: '-0.025em',
              background: 'linear-gradient(110deg, #c90072 0%, #a90086 42%, #176fc1 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Una nueva forma de comprender el talento de tu organización
          </Typography>
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            <P>
              Mapas Descriptivos transforma los resultados de las evaluaciones en una visión clara y visual de las
              personas y sus equipos, ayudándote a detectar patrones, fortalezas y oportunidades de desarrollo.
            </P>
            <P>Esta funcionalidad no está incluida en la suscripción actual de tu empresa.</P>
          </Box>
          <Button
            variant="contained"
            onClick={handleRequestDescriptiveMapsInfo}
            disabled={requestingInfo}
            sx={{ mt: 4, px: 4, py: 1.25, fontWeight: 700 }}
          >
            {requestingInfo ? 'Enviando…' : 'Solicitar información'}
          </Button>
        </Box>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      <Box sx={{ padding: '2% 0', width: '100%' }}>
        <H2 marginBottom="24px">Mapas Descriptivos</H2>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          sx={{
            borderBottom: '1px solid #d7e0ea',
            marginBottom: 3,
            '& .MuiTabs-indicator': { backgroundColor: '#c6016b' },
          }}
        >
          <Tab label="Personas" sx={{ color: '#163247', fontFamily: 'Public Sans', fontWeight: 700 }} />
          <Tab label="Equipos" sx={{ color: '#163247', fontFamily: 'Public Sans', fontWeight: 700 }} />
          <Tab label="Organigrama" sx={{ color: '#163247', fontFamily: 'Public Sans', fontWeight: 700 }} />
        </Tabs>

        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            mb: 3,
            p: 2,
            border: '1px solid #c9dcea',
            borderRadius: 2,
            background: 'linear-gradient(100deg, rgba(239, 245, 251, 0.95), rgba(255, 255, 255, 0.98))',
          }}
        >
          <Box>
            <Typography fontWeight={700} color="#17374D">
              ¿Quieres conectar esta información con otra plataforma?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Solicita una integración para que Bluesite pueda consumir sus datos y ampliar tus análisis.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleRequestIntegrationInfo}
            disabled={requestingInfo}
            sx={{ flexShrink: 0, fontWeight: 700, textTransform: 'none' }}
          >
            {requestingInfo ? 'Enviando…' : 'Solicitar integración'}
          </Button>
        </Box>

        {tab === 0 && (
          <Box>
            <Typography color="text.secondary" marginBottom={2}>
              Personas de la empresa con BETESA y/o 3D Skills finalizada. El desempeño se guarda para toda la empresa.
            </Typography>
            <TextField
              label="Buscar por nombre, correo o documento"
              value={peopleSearch}
              onChange={(event) => setPeopleSearch(event.target.value)}
              sx={{ width: 520, maxWidth: '100%', marginBottom: 2, backgroundColor: '#fff' }}
            />
            {peopleLoading ? (
              <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ border: '1px solid #d7e0ea', borderRadius: '18px', overflow: 'auto', background: '#fff' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      '180px minmax(240px, 1.2fr) minmax(220px, 1.15fr) minmax(240px, 1.25fr) 160px 160px',
                    gridTemplateAreas: '"baremo name cargo email document tests"',
                    gap: 1.5,
                    padding: '10px 16px',
                    background: '#eef5fb',
                    color: '#17374D',
                    fontWeight: 700,
                    minWidth: 1000,
                  }}
                >
                  <Typography sx={{ gridArea: 'name', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Nombre
                  </Typography>
                  <Typography sx={{ gridArea: 'email', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Correo
                  </Typography>
                  <Typography sx={{ gridArea: 'cargo', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Cargo
                  </Typography>
                  <Typography sx={{ gridArea: 'document', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Documento
                  </Typography>
                  <Typography sx={{ gridArea: 'tests', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Pruebas realizadas
                  </Typography>
                  <Typography sx={{ gridArea: 'baremo', color: '#17374D', fontSize: '0.875rem', fontWeight: 700 }}>
                    Desempeño
                  </Typography>
                </Box>
                {people.length === 0 ? (
                  <Box padding={3}>
                    <Typography>
                      {peopleSearch.trim()
                        ? 'No se encontraron personas elegibles.'
                        : 'Escribe un nombre, correo o documento para buscar personas.'}
                    </Typography>
                  </Box>
                ) : (
                  people.map((person) => (
                    <Box
                      key={person.user_id}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns:
                          '180px minmax(240px, 1.2fr) minmax(220px, 1.15fr) minmax(240px, 1.25fr) 160px 160px',
                        gridTemplateAreas: '"baremo name cargo email document tests"',
                        gap: 1.5,
                        alignItems: 'center',
                        padding: '6px 16px',
                        borderTop: '1px solid #e3ebf2',
                        minWidth: 1000,
                      }}
                    >
                      <Typography
                        noWrap
                        fontWeight={600}
                        sx={{ gridArea: 'name', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={person.full_name}
                      >
                        {person.full_name}
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        value={cargoDrafts[person.user_id] || ''}
                        onChange={(event) =>
                          setCargoDrafts((current) => ({ ...current, [person.user_id]: event.target.value }))
                        }
                        onBlur={() => saveCargo(person)}
                        placeholder="Cargo"
                        inputProps={{ maxLength: 500 }}
                        sx={{ gridArea: 'cargo', width: '100%', backgroundColor: '#fff' }}
                      />
                      <Typography
                        noWrap
                        color="text.secondary"
                        sx={{ gridArea: 'email', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={person.email || 'Sin correo'}
                      >
                        {person.email || 'Sin correo'}
                      </Typography>
                      <Typography
                        noWrap
                        color="text.secondary"
                        sx={{ gridArea: 'document', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={person.document || 'Sin documento'}
                      >
                        {person.document || 'Sin documento'}
                      </Typography>
                      <Typography
                        noWrap
                        color="text.secondary"
                        sx={{ gridArea: 'tests', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {person.completed_tests.join(', ')}
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        inputProps={{ inputMode: 'decimal' }}
                        value={baremoDrafts[person.user_id] || ''}
                        onChange={(event) => {
                          const nextValue = event.target.value
                          const numericValue = Number(nextValue.replace(',', '.'))
                          const validFormat = /^\d{0,2}([.,]\d{0,2})?$/.test(nextValue)
                          if (
                            !validFormat ||
                            (nextValue !== '' && Number.isFinite(numericValue) && numericValue > 10)
                          ) {
                            setBaremoErrors((current) => ({
                              ...current,
                              [person.user_id]: 'Solo se permiten valores de 1,00 a 10,00.',
                            }))
                            return
                          }
                          setBaremoDrafts((current) => ({ ...current, [person.user_id]: nextValue }))
                          setBaremoErrors((current) => ({ ...current, [person.user_id]: '' }))
                        }}
                        onBlur={() => saveBaremo(person)}
                        error={Boolean(baremoErrors[person.user_id])}
                        helperText={baremoErrors[person.user_id] || undefined}
                        placeholder="1,00 - 10,00"
                        sx={{ gridArea: 'baremo', width: 140, backgroundColor: '#fff' }}
                      />
                    </Box>
                  ))
                )}
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
              <Box>
                <Typography color="text.secondary">
                  Agrupa personas con BETESA o 3D Skills finalizada para preparar sus analíticas.
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={openCreate}
                sx={{
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
                  boxShadow: '0 14px 24px rgba(198, 1, 107, 0.2)',
                  fontFamily: 'Public Sans',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { background: 'linear-gradient(135deg, #b00060 0%, #7b1c50 100%)' },
                }}
              >
                Crear equipo
              </Button>
            </Box>
            {loading ? (
              <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
              </Box>
            ) : teams.length === 0 ? (
              <Box sx={{ padding: 4, border: '1px solid #d7e0ea', borderRadius: '18px', background: '#fff' }}>
                <Typography>No hay equipos creados todavía.</Typography>
              </Box>
            ) : (
              <TableContainer sx={{ border: '1px solid #d7e0ea', borderRadius: '18px', background: '#fff' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#eef5fb' }}>
                    <TableRow>
                      <TableCell sx={{ color: '#17374D', fontWeight: 700 }}>Equipo</TableCell>
                      <TableCell sx={{ color: '#17374D', fontWeight: 700 }}>Descripción</TableCell>
                      <TableCell sx={{ color: '#17374D', fontWeight: 700 }}>Personas</TableCell>
                      <TableCell align="right" sx={{ color: '#17374D', fontWeight: 700 }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teams.map((team) => (
                      <TableRow key={team.id} hover>
                        <TableCell sx={{ fontWeight: 700 }}>{team.name}</TableCell>
                        <TableCell>{team.description || 'Sin descripción'}</TableCell>
                        <TableCell>{team.member_count || 0}</TableCell>
                        <TableCell align="right">
                          <Box display="flex" justifyContent="flex-end" gap={1}>
                            <Button
                              size="small"
                              onClick={() => handleSyncTeam(team)}
                              disabled={syncingTeamId === team.id}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              {syncingTeamId === team.id ? 'Enviando...' : 'Mapas Descriptivos'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleSyncNineboxTeam(team)}
                              disabled={syncingNineboxTeamId === team.id}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              {syncingNineboxTeamId === team.id ? 'Enviando...' : '9Box'}
                            </Button>
                            <Button
                              size="small"
                              onClick={() => {
                                setOrganizationTeamId(team.id)
                                setTab(2)
                              }}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              Organigrama
                            </Button>
                            <Button
                              size="small"
                              onClick={() => openEdit(team.id)}
                              sx={{
                                color: '#2d749c',
                                fontFamily: 'Public Sans',
                                fontWeight: 700,
                                textTransform: 'none',
                              }}
                            >
                              Editar
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => removeTeam(team)}
                              sx={{ fontFamily: 'Public Sans', fontWeight: 700, textTransform: 'none' }}
                            >
                              Eliminar
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {tab === 2 && (
          <Box>
            <Typography color="text.secondary" marginBottom={2}>
              Define visualmente quién reporta a quién. Una persona puede coordinar a tantas personas como sea
              necesario.
            </Typography>
            <FormControl sx={{ minWidth: 320, marginBottom: 3 }}>
              <InputLabel id="lumina-team-select">Equipo</InputLabel>
              <Select
                labelId="lumina-team-select"
                label="Equipo"
                value={organizationTeamId}
                onChange={(event) => setOrganizationTeamId(Number(event.target.value))}
              >
                {teams.map((team) => (
                  <MenuItem key={team.id} value={team.id}>
                    {team.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!organizationTeamId ? (
              <Box sx={{ padding: 4, border: '1px solid #d7e0ea', borderRadius: 2, background: '#fff' }}>
                <Typography>Selecciona un equipo para crear su organigrama.</Typography>
              </Box>
            ) : organizationLoading ? (
              <Box display="flex" justifyContent="center" padding={6}>
                <CircularProgress />
              </Box>
            ) : organizationMembers.length === 0 ? (
              <Box sx={{ padding: 4, border: '1px solid #d7e0ea', borderRadius: 2, background: '#fff' }}>
                <Typography>Este equipo no tiene personas asignadas.</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  border: '1px solid #d7e0ea',
                  borderRadius: 3,
                  background: '#f7fbff',
                  overflow: 'auto',
                  padding: 3,
                }}
              >
                <Box display="flex" justifyContent="flex-end" marginBottom={3}>
                  <Button
                    variant="contained"
                    disabled={organizationSaving}
                    onClick={saveOrganization}
                    sx={{
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
                      textTransform: 'none',
                    }}
                  >
                    {organizationSaving ? 'Guardando...' : 'Guardar organigrama'}
                  </Button>
                </Box>
                <Box display="flex" justifyContent="center" gap={4} minWidth="max-content" paddingBottom={2}>
                  {organizationRoots.map((member) => (
                    <OrganizationNode
                      key={member.member_id}
                      member={member}
                      members={organizationMembers}
                      childrenByManager={childrenByManager}
                      getForbiddenManagerIds={getForbiddenManagerIds}
                      onManagerChange={changeManager}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        fullScreen
        PaperProps={{ sx: { backgroundColor: '#f7fbff' } }}
      >
        <DialogTitle>{form.id ? 'Editar equipo' : 'Crear equipo'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nombre"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Descripción"
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            margin="normal"
            multiline
            minRows={2}
          />
          <Typography fontWeight={700} marginTop={3}>
            Personas elegibles
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={1}>
            Solo se muestran personas de la compañía con BETESA o 3D Skills finalizada.
          </Typography>
          <Typography variant="body2" color="text.secondary" marginBottom={1}>
            Escribe para buscar; no se cargan todos los trabajadores automáticamente.
          </Typography>
          <TextField
            fullWidth
            label="Buscar por nombre, correo o documento"
            value={memberSearch}
            onChange={(event) => {
              const value = event.target.value
              setMemberSearch(value)
              if (!value.trim()) setMembers(form.id ? members : [])
            }}
            margin="normal"
            sx={{ maxWidth: 680, backgroundColor: '#fff' }}
          />
          <List
            dense
            sx={{
              height: 'calc(100vh - 390px)',
              minHeight: 280,
              overflowY: 'auto',
              border: '1px solid #d9e3ea',
              borderRadius: 1,
              backgroundColor: '#fff',
            }}
          >
            {membersLoading && (
              <ListItem>
                <CircularProgress size={20} />
                <ListItemText primary="Buscando personas..." sx={{ marginLeft: 2 }} />
              </ListItem>
            )}
            {!membersLoading &&
              members.map((member) => (
                <ListItem key={member.user_id} disablePadding>
                  <FormControlLabel
                    sx={{ width: '100%', margin: 0, padding: '4px 12px' }}
                    control={
                      <Checkbox
                        checked={form.memberIds.includes(member.user_id)}
                        onChange={() => toggleMember(member.user_id)}
                      />
                    }
                    label={
                      <ListItemText
                        primary={member.full_name}
                        secondary={`${member.email || member.document || 'Sin dato'} · ${(
                          member.completed_tests || []
                        ).join(', ')}`}
                      />
                    }
                  />
                </ListItem>
              ))}
            {!membersLoading && members.length === 0 && !memberSearch.trim() && (
              <ListItem>
                <ListItemText primary="Escribe un nombre, correo o documento para buscar personas." />
              </ListItem>
            )}
            {!membersLoading && members.length === 0 && memberSearch.trim() && (
              <ListItem>
                <ListItemText primary="No se encontraron personas elegibles." />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={saveTeam}
            sx={{
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
              textTransform: 'none',
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  )
}

export default LuminaAnalytics
